<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    Btn,
    InputText,
    ToggleSwitch,
    Tooltip,
} from '@jennifersoft/vue-components-v2';
import { apiClient } from '@/shared/services/api-client';
import { useServerStore } from '@/shared/stores/session/useServer';
import { usePasswordPolicy } from '@/shared/composables/usePasswordPolicy';
import {
    buildChartFrameLoginWindowUrl,
    CHART_FRAME_LOGIN_LAYOUT,
    CHART_FRAME_LOGIN_WINDOW_NAME,
    decodeChartFrameRedirect,
    isChartFrameLoginDone,
    isSafeChartFrameRedirect,
    listenChartFrameLogin,
} from '@jennifersoft/apm-core/chart-frame';
import UserLayout from '../common/UserLayout.vue';
import PasswordInput from '../common/PasswordInput.vue';
import Alert from '../common/Alert.vue';
import SignUp from './components/SignUp.vue';
import Succeeded from './components/Succeeded.vue';
import ChartFrameLoginState from './components/ChartFrameLoginState.vue';
import {
    CHART_FRAME_POPUP_CHECK_INTERVAL_MS,
    CHART_FRAME_POPUP_CLOSE_GRACE_MS,
} from './chart-frame-login-constants';
import type { ChartFrameLoginState as LoginState } from './chart-frame-login-types';

const props = withDefaults(
    defineProps<{
        logoUrl: string;
        redirectUrl: string;
        useSignIn: boolean;
        useSignUp: boolean;
        useAutoLogin: boolean;
        messageKey?: string;
        layout?: string;
    }>(),
    {
        messageKey: '',
        layout: '',
    }
);

const { t } = useI18n();
const serverStore = useServerStore();
const { validatePassword, fetchPolicy, policy } = usePasswordPolicy();

const keepLoginStorageKey = 'autoLogin';

// State
const sequence = ref(0);
const userId = ref('');
const userPassword = ref('');
const keepLogin = ref(false);
const alertMessage = ref<string | null>(
    props.messageKey ? t(props.messageKey) : null
);
const signUpStatus = ref<{ code: string | null; message: string } | null>(null);
const loading = ref(false);
const loginForm = ref<HTMLFormElement>();

// Computed
const loginSucceeded = computed(() => props.messageKey === '');
const isSucceeded = computed(() => sequence.value === 2);
const isSignUpForm = computed(() => sequence.value === 1);
const isSignIn = computed(() => sequence.value === 0);
// 마운트 시점에 한 번만 결정되는 폼 action.
// reactive 하면 토글 변경 즉시 /login/auto 로 바뀌어 첫 등록(쿠키 미존재)에서 실패하므로 의도적으로 고정.
// /login/auto 는 쿠키 기반(id/password 폼 무시), /login/page 는 폼 기반(autoLogin 파라미터로 쿠키 발급).
const loginUrl = ref('/login/page');
const jenniferK8s = computed(() => {
    return (
        serverStore.jenniferK8s !== null &&
        serverStore.jenniferK8s !== 'null' &&
        serverStore.jenniferK8s !== ''
    );
});

// 차트 iframe 안에서 열린 로그인: 폼 대신 안내와 '새 창 로그인' 버튼만 보여준다.
const isChartFrameLogin = computed(
    () => props.layout === CHART_FRAME_LOGIN_LAYOUT
);
const chartFramePath = computed(() =>
    decodeChartFrameRedirect(props.redirectUrl)
);
const isChartFramePopupRetry = computed(() => {
    const queryIndex = chartFramePath.value.indexOf('?');
    return (
        isSafeChartFrameRedirect(chartFramePath.value) &&
        queryIndex >= 0 &&
        isChartFrameLoginDone(chartFramePath.value.slice(queryIndex))
    );
});
let stopChartFrameLoginListener: (() => void) | null = null;
let chartFrameReloading = false;
const chartFrameLoginState = ref<LoginState>('idle');
let chartFramePopupTimer: ReturnType<typeof setInterval> | null = null;

const stopChartFramePopupTimer = () => {
    if (chartFramePopupTimer !== null) clearInterval(chartFramePopupTimer);
    chartFramePopupTimer = null;
};

const reloadChartFrame = () => {
    if (chartFrameReloading || !isSafeChartFrameRedirect(chartFramePath.value))
        return;
    chartFrameReloading = true;
    chartFrameLoginState.value = 'complete';
    stopChartFramePopupTimer();
    window.location.replace(chartFramePath.value);
};

const openChartFrameLoginWindow = () => {
    if (isChartFramePopupRetry.value) {
        window.location.assign(chartFramePath.value);
        return;
    }
    stopChartFramePopupTimer();
    try {
        const popup = window.open(
            buildChartFrameLoginWindowUrl(chartFramePath.value),
            CHART_FRAME_LOGIN_WINDOW_NAME
        );
        if (!popup) {
            chartFrameLoginState.value = 'blocked';
            return;
        }
        chartFrameLoginState.value = 'waiting';
        const openedAt = Date.now();
        // 팝업 닫힘은 안내만 갱신한다. 인증 확인과 복귀는 기존 신호가 담당한다.
        chartFramePopupTimer = setInterval(() => {
            if (!popup.closed) return;
            stopChartFramePopupTimer();
            // 열자마자 닫힘이면 창 참조가 끊긴 것이라 실제 닫힘을 알 수 없다. 대기 상태를 둔다.
            if (Date.now() - openedAt < CHART_FRAME_POPUP_CLOSE_GRACE_MS)
                return;
            if (!chartFrameReloading) chartFrameLoginState.value = 'closed';
        }, CHART_FRAME_POPUP_CHECK_INTERVAL_MS);
    } catch {
        chartFrameLoginState.value = 'blocked';
    }
};

const cancelChartFrameLogin = () => window.close();

// 신호를 놓친 경우(다른 탭에서 로그인 등) 화면에 다시 보일 때 로그인 여부를 한 번 확인한다.
// 로그인 상태면 차트 주소가 /login 으로 리다이렉트되지 않는다.
const recheckChartFrameLogin = async () => {
    if (document.visibilityState !== 'visible') return;
    if (!isSafeChartFrameRedirect(chartFramePath.value)) return;
    try {
        const response = await fetch(chartFramePath.value, {
            method: 'GET',
            redirect: 'manual',
            credentials: 'same-origin',
        });
        if (response.type !== 'opaqueredirect' && response.ok) {
            reloadChartFrame();
        }
    } catch (e) {
        // 인증 결과는 바꾸지 않고 다음 시도를 위한 안내만 표시한다.
        if (!chartFrameReloading) chartFrameLoginState.value = 'unavailable';
    }
};

// Methods
const checkedAutoLogin = () => {
    if (props.messageKey !== '') {
        localStorage.removeItem(keepLoginStorageKey);
        return false;
    }
    return (
        localStorage.getItem(keepLoginStorageKey) === 'true' &&
        !jenniferK8s.value
    );
};

// Initialize keepLogin
keepLogin.value = checkedAutoLogin();
// 초기 자동로그인 상태(=쿠키 존재 추정)일 때만 onMounted 의 silent 자동로그인 경로(/login/auto)로 보냄.
// 사용자가 폼에서 토글을 변경해도 폼 action 은 그대로 /login/page 를 유지 → 쿠키 미존재 사용자도 정상 등록 가능.
if (keepLogin.value) {
    loginUrl.value = '/login/auto';
}

const onChangeAutoLogin = (checked: boolean) => {
    localStorage.setItem(keepLoginStorageKey, String(checked));
    keepLogin.value = checked;
};

const login = async () => {
    if (!keepLogin.value) {
        if (userId.value === '') {
            alertMessage.value = t('M0001');
            return;
        }
        if (userPassword.value === '') {
            alertMessage.value = t('M0002');
            return;
        }

        loading.value = true;
        try {
            const response = await apiClient.post<boolean>(
                '/login/duplicate',
                {
                    body: {
                        format: 'json',
                        id: userId.value,
                    },
                    payloadType: 'form-data',
                }
            );

            if (response.ok && response.data === true) {
                alertMessage.value = t('M0292');
            }
        } catch (e) {
            // Ignore duplicate check error and proceed to login
        } finally {
            loading.value = false;
        }
    }

    // Submit form natively
    if (loginForm.value) {
        loginForm.value.submit();
    }
};

const submitSignForm = async (payload: any) => {
    loading.value = true;
    try {
        const response = await apiClient.post<string>(
            '/login/user/save',
            {
                body: payload,
                payloadType: 'form-data',
            }
        );

        if (response.ok) {
            const data = response.data;
            if (data === 'M0107') {
                sequence.value++;
            }

            // Validate policy or get message
            if (data === 'M0104') {
                // INVALID_PASSWORD
                const validation = validatePassword(payload.password);
                if (!validation.isValid && validation.code) {
                    signUpStatus.value = {
                        code: validation.code,
                        message: validation.message,
                    };
                } else {
                    let msg = t(data || 'M0104');
                    if (policy.value) {
                        msg = msg.replace(
                            '%d',
                            String(policy.value.passwordLength)
                        );
                        msg = msg.replace(
                            '%d',
                            String(policy.value.minLowercase)
                        );
                        msg = msg.replace(
                            '%d',
                            String(policy.value.minUppercase)
                        );
                        msg = msg.replace('%d', String(policy.value.minDigit));
                        msg = msg.replace(
                            '%d',
                            String(policy.value.minSpecial)
                        );
                        msg = msg.replace(
                            '%d',
                            String(policy.value.minSameCount)
                        );
                    }
                    signUpStatus.value = {
                        code: data,
                        message: msg,
                    };
                }
            } else {
                signUpStatus.value = {
                    code: data,
                    message: data ? t(data) : '',
                };
            }
        } else {
            // Handle non-OK response (e.g. 500 error)
            signUpStatus.value = {
                code: 'M0003', // Fallback error code (Server Error)
                message: t('M0003'),
            };
        }
    } catch (e) {
        signUpStatus.value = {
            code: 'M0003',
            message: t('M0003'),
        };
    } finally {
        loading.value = false;
    }
};

const ifSupportBrowserNLogin = () => {
    login();
};

const goNext = () => {
    sequence.value++;
    if (sequence.value > 2) sequence.value = 0;
    signUpStatus.value = null;
    alertMessage.value = null;
};

const goBack = () => {
    sequence.value--;
    signUpStatus.value = null;
    alertMessage.value = null;
};

onMounted(async () => {
    if (isChartFrameLogin.value) {
        stopChartFrameLoginListener = listenChartFrameLogin(reloadChartFrame);
        document.addEventListener('visibilitychange', recheckChartFrameLogin);
        window.addEventListener('focus', recheckChartFrameLogin);
        return;
    }
    if (loginSucceeded.value && keepLogin.value) {
        login();
    }
    // Fetch password policy for validation
    await fetchPolicy();
});

onBeforeUnmount(() => {
    stopChartFramePopupTimer();
    stopChartFrameLoginListener?.();
    document.removeEventListener('visibilitychange', recheckChartFrameLogin);
    window.removeEventListener('focus', recheckChartFrameLogin);
});
</script>

<template>
    <ChartFrameLoginState
        v-if="isChartFrameLogin"
        :state="chartFrameLoginState"
        :popup-retry="isChartFramePopupRetry"
        :error-message="alertMessage"
        @login="openChartFrameLoginWindow"
        @cancel="cancelChartFrameLogin"
    />
    <UserLayout
        v-else
        :logo-url="logoUrl"
        :show-footer="isSignIn"
        :expanded="isSignUpForm"
        :loading="loading"
    >
        <Succeeded v-if="isSucceeded" @login="goNext" />
        <SignUp
            v-else-if="isSignUpForm"
            :sign-up-status="signUpStatus"
            @submit="submitSignForm"
            @hide="goBack"
        />
        <div class="login-default" v-else>
            <div class="title">{{ t('ui.button.login') }}</div>
            <div class="description" v-if="useSignIn">
                <template v-if="useSignUp">
                    <span style="margin-right: 8px">{{ t('M0638') }}</span>
                    <span class="link" @click="goNext">
                        {{ t('M0492') }}
                    </span>
                </template>
            </div>
            <form
                class="form-group"
                :action="loginUrl"
                method="post"
                ref="loginForm"
                v-if="useSignIn"
            >
                <InputText
                    v-model="userId"
                    name="id"
                    :placeholder="t('ui.label.userId')"
                    autocomplete="username"
                    @keyup.enter="ifSupportBrowserNLogin"
                    size="large"
                    style="width: 100%"
                />
                <PasswordInput
                    v-model="userPassword"
                    name="password"
                    :placeholder="t('ui.label.password')"
                    @keyup:enter="ifSupportBrowserNLogin"
                    size="large"
                />
                <div v-if="useAutoLogin" class="aries-switch-wrapper">
                    <Tooltip
                        :text="t('text.0177')"
                        placement="top"
                        :disabled="!jenniferK8s"
                    >
                        <ToggleSwitch
                            :model-value="keepLogin"
                            :disabled="jenniferK8s"
                            @update:model-value="onChangeAutoLogin"
                        />
                    </Tooltip>
                    <span>{{ t('ui.label.keepLogin') }}</span>
                </div>
                <!-- ToggleSwitch 의 name 속성이 내부 checkbox 로 전달되지 않아, 폼 전송 시 hidden input 으로 명시 -->
                <input type="hidden" name="autoLogin" :value="keepLogin" />
                <input type="hidden" name="redirect" :value="redirectUrl" />
            </form>
            <div class="footer" v-if="useSignIn">
                <Btn
                    class="login-btn"
                    type="primary-filled"
                    size="large"
                    :text="t('ui.button.login')"
                    @click="ifSupportBrowserNLogin"
                />
                <Alert
                    v-if="!!alertMessage"
                    :message="alertMessage"
                    type="danger"
                    fade-down
                />
            </div>
            <div class="message" v-if="!useSignIn">{{ t('M0574') }}</div>
        </div>
    </UserLayout>
</template>

<style lang="scss">
@use '@/features/user/common/index.scss' as *;

.login-default {
    display: flex;
    flex-direction: column;

    .title {
        font-size: 32px;
        font-weight: bold;
        color: var(--gray-900, #222229);
        margin-bottom: 16px;
    }
    .description {
        font-size: 16px;
        line-height: 1.5;
        color: var(--gray-600, #75757c);
        .link {
            color: var(--primary-700, #8652ff);
            cursor: pointer;
        }
    }

    .form-group {
        margin: 40px 0;
        > :first-child {
            margin-bottom: 16px;
        }
        > .aries-switch-wrapper {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 40px;
            font-size: 14px;
        }
    }
    .footer {
        margin-bottom: 40px;
        display: flex;
        flex-direction: column;

        .login-btn {
            background: linear-gradient(
                135deg,
                var(--purple-700) 0%,
                var(--blue-700) 100%
            );
            border: none;
            border-radius: 12px;
            color: var(--gray-50);
            font-weight: 600;
            font-size: 0.95rem;
            box-shadow: 0 4px 12px rgba(134, 82, 255, 0.2);
            position: relative;
            overflow: hidden;

            &::after {
                content: '';
                position: absolute;
                top: 0;
                left: -250px;
                width: 200px;
                height: 100%;
                background: linear-gradient(
                    to right,
                    rgba(255, 255, 255, 0) 0%,
                    rgba(255, 255, 255, 0.05) 50%,
                    rgba(255, 255, 255, 0) 100%
                );
                transform: skewX(-25deg);
                transition: none;
            }

            &:hover {
                opacity: 0.9;

                &::after {
                    animation: shine 2.5s ease-in-out infinite;
                }
            }
        }
    }

    .message {
        font-size: 14px;
        color: var(--gray-600);
    }
}

@keyframes shine {
    100% {
        left: 150%;
    }
}
</style>
